import React, { useState, useMemo, useCallback } from "react";
import useDashboardData from "../../hooks/useDashboardData";
import useRentBilled from "../../hooks/useRentBilled";
import { mapDashboardSeries } from "../../utils/chart/mapDashboardSeries";
import { colorForLabel } from "../../utils/chart/seriesPalette";
import ChartFrame from "../../components/dashboard/ChartFrame";
import StatChart from "../../components/charts/StatChart";

const fmtCurrency = (v) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000)
    return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (Math.abs(n) >= 10_000)
    return `$${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const RentChart = ({
  title,
  propertyValue,
  selectedStartDate,
  selectedEndDate,
  chartHeight = 280,
}) => {
  const propertyId =
    propertyValue && propertyValue.value ? propertyValue.value : "";
  const range = useMemo(
    () => ({ start: selectedStartDate, end: selectedEndDate }),
    [selectedStartDate, selectedEndDate]
  );

  const { data, loading, error, needsProperty } = useRentBilled(
    propertyId,
    range
  );

  const series = useMemo(
    () => [
      {
        name: "Rent Billed",
        data: data.monthly,
        color: colorForLabel("collected", 0),
      },
    ],
    [data.monthly]
  );

  if (needsProperty) {
    return (
      <ChartFrame
        title={title}
        subtitle="Aggregated from Mastersheet"
        isEmpty
        emptyTitle="Select a property"
        emptyBody="Choose a property in the filter above to see rent billed by month."
      />
    );
  }

  const isEmpty =
    !loading && !error && (data.monthly.length === 0 || data.total === 0);

  const subtitle =
    !loading && data.total > 0
      ? `${fmtCurrency(data.total)} billed across ${data.monthly.filter(Boolean).length} months`
      : "Aggregated from Mastersheet";

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      series={!isEmpty && !loading && !error ? series : undefined}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyTitle="No rent records"
      emptyBody="No active leases in the selected range for this property."
    >
      <StatChart
        type="bar"
        categories={data.categories}
        series={series}
        valueFormatter={fmtCurrency}
        ariaLabel={`${title} rent billed by month`}
        height={chartHeight}
      />
    </ChartFrame>
  );
};

const StackedBarChartCard = ({
  selectedStartDate,
  selectedEndDate,
  type,
  title,
  selectedProperty,
  propertyManagerID,
  chartHeight = 280,
}) => {
  const filters = useMemo(
    () => ({
      selectedStartDate,
      selectedEndDate,
      propertyManagerID,
      selectedProperty,
    }),
    [selectedStartDate, selectedEndDate, propertyManagerID, selectedProperty]
  );

  const { data, loading, error } = useDashboardData(type, filters);

  const { categories, series, isEmpty } = useMemo(
    () => mapDashboardSeries(type, data),
    [type, data]
  );

  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());

  const toggleSeries = useCallback((name) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  if (type === "rent") {
    return (
      <RentChart
        title={title}
        propertyValue={selectedProperty}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        chartHeight={chartHeight}
      />
    );
  }

  return (
    <ChartFrame
      title={title}
      series={series}
      hiddenSeries={hiddenSeries}
      onToggleSeries={toggleSeries}
      loading={loading}
      error={error}
      isEmpty={!loading && !error && isEmpty}
      emptyTitle="No data for this range"
      emptyBody="Try a wider date range or different filters."
    >
      <StatChart
        type="stackedBar"
        categories={categories}
        series={series}
        hiddenSeries={hiddenSeries}
        ariaLabel={`${title} stacked bar chart by month`}
        height={chartHeight}
      />
    </ChartFrame>
  );
};

export default StackedBarChartCard;
